import { createSignal } from "solid-js";
import type { IntakeId, WorkItemId } from "../api/types";

/**
 * The post-create summary FLOW-550 shows after "Commit approved items",
 * keyed by intake id.
 *
 * Kept outside any component for the same reason as `planSessionStore`: the
 * Plan page unmounts when the user checks the backlog, so a local signal
 * would lose the summary the moment they navigate away. Mirrored into
 * `sessionStorage` so a reload doesn't lose it either, and cleared with the
 * browser session.
 */
const STORAGE_KEY = "flowicity_plan_commits";

export interface CommittedItem {
  id: WorkItemId;
  item_number: number;
  title: string;
}

export interface CommittedSummary {
  /** One per approved group since FLOW-694; empty when no epic was created */
  epics: CommittedItem[];
  stories: CommittedItem[];
}

type Commits = Record<IntakeId, CommittedSummary>;

/** The pre-FLOW-694 stored shape, still sitting in a live sessionStorage */
type StoredSummary = CommittedSummary & { epic?: CommittedItem };

/**
 * Lift a summary written before FLOW-694 — one `epic`, not `epics` — into the
 * current shape. Storage outlives a deploy, and `<For each={undefined}>` would
 * quietly drop the epic row rather than fail loudly.
 */
function migrate(summary: StoredSummary): CommittedSummary {
  if (summary.epics) return summary;
  return {
    epics: summary.epic ? [summary.epic] : [],
    stories: summary.stories ?? [],
  };
}

function read(): Commits {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<IntakeId, StoredSummary>).map(
        ([intakeId, summary]) => [intakeId, migrate(summary)],
      ),
    ) as Commits;
  } catch {
    return {};
  }
}

function write(commits: Commits): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(commits));
  } catch {
    // Storage unavailable (private mode, quota) — in-memory state still works.
  }
}

const [commits, setCommits] = createSignal<Commits>(read());

export function committedSummary(
  intakeId: IntakeId,
): CommittedSummary | undefined {
  return commits()[intakeId];
}

export function setCommittedSummary(
  intakeId: IntakeId,
  summary: CommittedSummary,
): void {
  setCommits((prev) => {
    const next = { ...prev, [intakeId]: summary };
    write(next);
    return next;
  });
}
